import Phaser from 'phaser';
import {
  FLOOR_ATLAS, FLOOR_LAYERS, MAX_FLOOR_DIMENSION, floorCellFrame, overlayFringeFrame,
  type FloorLayerData, type FloorLayerName,
} from '@campus/core';
import { extrudeTileTexture, EXTRUDED_MARGIN, EXTRUDED_SPACING } from './extrudeTiles.js';

export const FLOOR_ATLAS_KEY = 'floors-atlas';

/**
 * Profondeurs des couches « au sol », de bas en haut — toutes sous les
 * avatars (tri en Y à partir de 100) :
 * sol du TMJ < sol peint < revêtements peints < objets au sol (tapis)
 * < étiquettes de texte < autres calques normaux du TMJ (mobilier en tuiles,
 * bas de murs…).
 */
export const FLOOR_DEPTHS = {
  tmjFloor: 1,
  floor: 2,
  overlay: 3,
  /** Bordures des revêtements inversés (« Pelouse (bord naturel) »), dessinées sur les cases voisines. */
  overlayFringe: 3.5,
  floorObjects: 4,
  /** Étiquettes de texte (outil « Texte ») : sur les tapis, sous le mobilier du TMJ. */
  labels: 4.5,
  tmjOther: 5,
} as const;

/** À appeler dans preload() de toute scène qui affiche les sols peints. */
export function preloadFloorAtlas(scene: Phaser.Scene): void {
  if (scene.textures.exists(FLOOR_ATLAS_KEY)) return;
  scene.load.image(FLOOR_ATLAS_KEY, FLOOR_ATLAS.url);
}

/**
 * Rendu des couches de sol peintes (`map_floor_layers`), partagé
 * ZoneScene / EditorScene.
 *
 * Une vraie couche de tuiles Phaser par couche (pas un sprite par case comme
 * les murs) : un sol peut couvrir toute la carte (150×150 = 22 500 cases).
 * Tilemap vierge à la taille max, frames de l'atlas = index de tuile
 * (tileset en gid 0).
 */
export class FloorRenderer {
  private readonly layers = new Map<FloorLayerName, Phaser.Tilemaps.TilemapLayer>();
  private readonly fringe: Phaser.Tilemaps.TilemapLayer | null;

  constructor(scene: Phaser.Scene, depths: Record<FloorLayerName, number>) {
    const ts = FLOOR_ATLAS.tileSize;
    const map = scene.make.tilemap({
      tileWidth: ts, tileHeight: ts, width: MAX_FLOOR_DIMENSION, height: MAX_FLOOR_DIMENSION,
    });
    // Atlas extrudé : pas de traits entre les tuiles aux zooms non entiers.
    const atlasKey = extrudeTileTexture(scene, FLOOR_ATLAS_KEY, ts, ts);
    const tileset = map.addTilesetImage('floors', atlasKey, ts, ts, EXTRUDED_MARGIN, EXTRUDED_SPACING, 0);
    if (!tileset) throw new Error('[FloorRenderer] atlas des sols non chargé (preloadFloorAtlas oublié ?)');
    for (const name of FLOOR_LAYERS) {
      const layer = map.createBlankLayer(`painted-${name}`, tileset);
      if (!layer) continue;
      layer.setDepth(depths[name]);
      this.layers.set(name, layer);
    }
    this.fringe = map.createBlankLayer('painted-overlay-fringe', tileset);
    this.fringe?.setDepth(FLOOR_DEPTHS.overlayFringe);
  }

  /** Redessine toute une couche. */
  render(name: FloorLayerName, data: FloorLayerData): void {
    const layer = this.layers.get(name);
    if (!layer) return;
    layer.fill(-1);
    if (name === 'overlay') this.fringe?.fill(-1);
    // Une case de plus autour : la bordure d'un revêtement inversé peint au
    // bord de la grille déborde sur la case d'à côté.
    const pad = name === 'overlay' ? 1 : 0;
    const cache = new Map<number, string | null>();
    for (let y = -pad; y < data.height + pad; y++) {
      for (let x = -pad; x < data.width + pad; x++) this.putCell(layer, name, data, x, y, cache);
    }
  }

  /**
   * Redessine seulement des cases modifiées (éditeur, pendant un coup de
   * pinceau). L'apparence d'une case dépend de ses 8 voisines (raccords) ;
   * sur le Sol, le sable mouillé dépend en plus de l'eau à 2 cases — rayon 3.
   */
  renderCells(name: FloorLayerName, data: FloorLayerData, cells: Array<{ x: number; y: number }>): void {
    const layer = this.layers.get(name);
    if (!layer) return;
    const done = new Set<number>();
    const cache = new Map<number, string | null>();
    const radius = name === 'overlay' ? 1 : 3;
    for (const c of cells) {
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const x = c.x + dx, y = c.y + dy;
          const key = y * MAX_FLOOR_DIMENSION + x;
          if (done.has(key)) continue;
          done.add(key);
          this.putCell(layer, name, data, x, y, cache);
        }
      }
    }
  }

  setVisible(visible: boolean): void {
    for (const layer of this.layers.values()) layer.setVisible(visible);
    this.fringe?.setVisible(visible);
  }

  private putCell(
    layer: Phaser.Tilemaps.TilemapLayer, name: FloorLayerName, data: FloorLayerData, x: number, y: number,
    cache: Map<number, string | null>,
  ): void {
    if (x < 0 || y < 0 || x >= MAX_FLOOR_DIMENSION || y >= MAX_FLOOR_DIMENSION) return;
    const frame = floorCellFrame(name, data, x, y, cache);
    if (frame === 0) layer.removeTileAt(x, y);
    else layer.putTileAt(frame, x, y);
    if (name !== 'overlay' || !this.fringe) return;
    const fringe = overlayFringeFrame(data, x, y);
    if (fringe === 0) this.fringe.removeTileAt(x, y);
    else this.fringe.putTileAt(fringe, x, y);
  }
}
